import { FormEvent, useEffect, useRef, useState, KeyboardEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { z } from 'zod';
import { useAuth } from '@/providers/AuthProvider';
import { useLocation } from '@/providers/LocationProvider';
import {
  Profile,
  SelfProfileUpdatePayload,
  useSelfProfile,
  useUpsertSelfProfile,
} from '@/services/profiles';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { useTheme } from '@/theme/ThemeProvider';
import { useUiConfig } from '@/services/config';
import { AppVersion } from '@/components/AppVersion';
import styles from './ProfilePage.module.css';

interface ProfileFormState {
  name: string;
  dateOfBirth: string;
  description: string;
  city: string;
  stateProvince: string;
  school: string;
  interests: string[];
  preferredDistanceUnit: 'ft' | 'm';
}

const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;

const profileSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  dateOfBirth: z.string().regex(dateRegex, 'Enter date as YYYY-MM-DD.'),
  description: z.string().max(400, 'Description must be 400 characters or fewer'),
  city: z.string().max(80).optional(),
  stateProvince: z.string().max(80).optional(),
  school: z.string().max(120).optional(),
  interests: z.array(z.string().min(1)).min(1, 'Add at least one interest'),
  preferredDistanceUnit: z.enum(['ft', 'm']),
});

const defaultFormState: ProfileFormState = {
  name: '',
  dateOfBirth: '',
  description: '',
  city: '',
  stateProvince: '',
  school: '',
  interests: [],
  preferredDistanceUnit: 'ft',
};

const normalizeDateInput = (value?: string) => {
  if (!value) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
};

const toFormState = (profile?: Profile): ProfileFormState => {
  if (!profile) {
    return defaultFormState;
  }
  return {
    name: profile.name ?? '',
    dateOfBirth: normalizeDateInput(profile.dateOfBirth),
    description: profile.description ?? '',
    city: (profile.metadata?.city as string) ?? '',
    stateProvince: (profile.metadata?.state as string) ?? '',
    school: (profile.metadata?.school as string) ?? '',
    interests: profile.interests ?? [],
    preferredDistanceUnit: profile.preferredDistanceUnit ?? 'ft',
  };
};

const normalizeInterestInput = (value: string) =>
  value
    .split(',')
    .map((interest) => interest.trim())
    .filter(Boolean);

const ProfilePage = () => {
  const { isAuthenticated, isLoading: authLoading, user, login, logout } = useAuth();
  const { themeType, locationEnabled } = useTheme();
  const { data: config } = useUiConfig();
  const { coords } = useLocation();
  const { data, isLoading, isError } = useSelfProfile({
    enabled: isAuthenticated,
  });
  const upsertSelfProfile = useUpsertSelfProfile();

  const [formState, setFormState] = useState<ProfileFormState>(defaultFormState);
  const [interestInput, setInterestInput] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const configAppliedRef = useRef(false);

  useEffect(() => {
    if (data) {
      setFormState(toFormState(data as Profile));
    }
  }, [data]);

  useEffect(() => {
    if (!config?.preferredDistanceUnit || configAppliedRef.current) {
      return;
    }
    if ((data as Profile)?.preferredDistanceUnit) {
      configAppliedRef.current = true;
      return;
    }
    const unit = config.preferredDistanceUnit === 'm' ? 'm' : 'ft';
    setFormState((prev) => ({ ...prev, preferredDistanceUnit: unit }));
    configAppliedRef.current = true;
  }, [config?.preferredDistanceUnit, (data as Profile)?.preferredDistanceUnit]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      Promise.resolve(login()).catch((error: unknown) => {
        console.warn('Auth login redirect failed', error);
      });
    }
  }, [authLoading, isAuthenticated, login]);

  const addInterests = (value: string) => {
    const interests = normalizeInterestInput(value);
    if (!interests.length) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      interests: Array.from(new Set([...prev.interests, ...interests])),
    }));
    setInterestInput('');
  };

  const handleInterestKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addInterests(interestInput);
    }
  };

  const removeInterest = (interest: string) => {
    setFormState((prev) => ({
      ...prev,
      interests: prev.interests.filter((item) => item !== interest),
    }));
  };

  const sanitizeOptional = (value?: string) => {
    const trimmed = value?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : undefined;
  };

  const handleLogout = () => {
    try {
      logout();
    } catch (error) {
      console.warn('Failed to log out', error);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const parsed = profileSchema.parse({ ...formState });

      const metadata: Record<string, unknown> = {
        ...((data as Profile)?.metadata ?? {}),
      };
      const normalizedCity = sanitizeOptional(parsed.city);
      const normalizedState = sanitizeOptional(parsed.stateProvince);
      const normalizedSchool = sanitizeOptional(parsed.school);

      if (normalizedCity) {
        metadata.city = normalizedCity;
      } else {
        delete metadata.city;
      }

      if (normalizedState) {
        metadata.state = normalizedState;
      } else {
        delete metadata.state;
      }

      if (normalizedSchool) {
        metadata.school = normalizedSchool;
      } else {
        delete metadata.school;
      }

      const location =
        coords != null
          ? (() => {
              const next: Profile['location'] = {
                latitude: coords.latitude,
                longitude: coords.longitude,
                timestamp: coords.timestamp ?? Date.now(),
              };
              if (typeof coords.accuracy === 'number') {
                next.accuracy = coords.accuracy;
              }
              return next;
            })()
          : (() => {
              const existing = (data as Profile)?.location
                ? { ...(data as Profile).location }
                : {
                    latitude: 0,
                    longitude: 0,
                    timestamp: Date.now(),
                  };
              if (!existing.timestamp) {
                existing.timestamp = Date.now();
              }
              return existing;
            })();

      const payload: SelfProfileUpdatePayload = {
        name: parsed.name,
        dateOfBirth: parsed.dateOfBirth,
        description: parsed.description,
        interests: parsed.interests,
        metadata,
        location,
        preferredDistanceUnit: formState.preferredDistanceUnit,
        themeType,
        enableLocationBasedTheme: locationEnabled,
      };

      if (user && typeof user === 'object' && 'email' in user && user.email) {
        payload.email = user.email as string;
      }

      const savedProfile = await upsertSelfProfile.mutateAsync(payload);

      setFormState(toFormState(savedProfile));

      setSubmitSuccess(true);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setSubmitError(
          error.issues[0]?.message ?? 'Check the highlighted fields and try again.',
        );
      } else if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Unable to save profile right now.');
      }
    }
  };

  if (authLoading) {
    return (
      <div className={styles.page}>
        <section className={styles.card}>
          <p>Preparing sign-in…</p>
        </section>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <Helmet>
          <title>Profile • AwkHerd</title>
        </Helmet>
        <section className={styles.card}>
          <h1>Sign in to manage your profile</h1>
          <p>You need to be logged in to update your AwkHerd profile.</p>
          <button type="button" onClick={login} className={styles.primaryButton}>
            Sign in with AwkHerd
          </button>
          <AppVersion className={styles.version} />
        </section>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Profile • AwkHerd</title>
      </Helmet>
      <header className={styles.header}>
        <h1>Your profile</h1>
        <p>Keep your details current so the herd can find you.</p>
        <AppVersion className={styles.versionInline} label="App" />
      </header>
      <section className={styles.card}>
        <div className={styles.layout}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.row}>
              <label htmlFor="email">Email</label>
              {user && typeof user === 'object' && 'email' in user ? user.email : 'N/A'}
            </div>

            <div className={styles.row}>
              <label htmlFor="profile-name">Display name</label>
              <input
                id="profile-name"
                value={formState.name}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  setFormState((prev) => ({ ...prev, name: value }));
                }}
                required
                minLength={2}
              />
            </div>

            <div className={styles.row}>
              <label htmlFor="profile-dob">Date of Birth</label>
              <input
                id="profile-dob"
                type="date"
                value={formState.dateOfBirth}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  setFormState((prev) => ({ ...prev, dateOfBirth: value }));
                }}
                max={new Date().toISOString().slice(0, 10)}
                required
              />
            </div>

            <div className={styles.row}>
              <label htmlFor="profile-description">About you</label>
              <textarea
                id="profile-description"
                value={formState.description}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  setFormState((prev) => ({
                    ...prev,
                    description: value,
                  }));
                }}
                maxLength={400}
                rows={5}
                placeholder="Tell the herd what you are into (max 400 characters)."
              />
              <span className={styles.help}>{formState.description.length}/400</span>
            </div>

            <div className={styles.row}>
              <label htmlFor="profile-interests">Interests</label>
              <div className={styles.interestInput}>
                <input
                  id="profile-interests"
                  value={interestInput}
                  onChange={(event) => setInterestInput(event.currentTarget.value)}
                  onKeyDown={handleInterestKeyDown}
                  placeholder="Add an interest and press Enter"
                />
                <button
                  type="button"
                  onClick={() => addInterests(interestInput)}
                  className={styles.secondaryButton}
                >
                  Add
                </button>
              </div>
              <div className={styles.chips}>
                {formState.interests.map((interest) => (
                  <span key={interest} className={styles.chip}>
                    {interest}
                    <button type="button" onClick={() => removeInterest(interest)}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.grid}>
              <div className={styles.row}>
                <label htmlFor="profile-city">City</label>
                <input
                  id="profile-city"
                  value={formState.city}
                  onChange={(event) => {
                    const { value } = event.currentTarget;
                    setFormState((prev) => ({ ...prev, city: value }));
                  }}
                  placeholder="City or town"
                />
              </div>
              <div className={styles.row}>
                <label htmlFor="profile-state">State / Province</label>
                <input
                  id="profile-state"
                  value={formState.stateProvince}
                  onChange={(event) => {
                    const { value } = event.currentTarget;
                    setFormState((prev) => ({
                      ...prev,
                      stateProvince: value,
                    }));
                  }}
                  placeholder="State or province"
                />
              </div>
              <div className={styles.row}>
                <label htmlFor="profile-school">School / Organization</label>
                <input
                  id="profile-school"
                  value={formState.school}
                  onChange={(event) => {
                    const { value } = event.currentTarget;
                    setFormState((prev) => ({ ...prev, school: value }));
                  }}
                  placeholder="Where do you spend your time?"
                />
              </div>
            </div>

            <div className={styles.row}>
              <label htmlFor="profile-distance-unit">Preferred Distance Unit</label>
              <select
                id="profile-distance-unit"
                value={formState.preferredDistanceUnit}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  const unit = value === 'm' ? 'm' : 'ft';
                  setFormState((prev) => ({ ...prev, preferredDistanceUnit: unit }));
                }}
              >
                <option value="ft">Feet</option>
                <option value="m">Meters</option>
              </select>
            </div>

            {submitError && <p className={styles.error}>{submitError}</p>}
            {submitSuccess && (
              <p className={styles.success}>Profile saved successfully.</p>
            )}

            <div className={styles.actions}>
              <button
                type="submit"
                className={styles.primaryButton}
                disabled={isLoading || upsertSelfProfile.isPending}
              >
                {data ? 'Save changes' : 'Create profile'}
              </button>
            </div>
          </form>

          <aside className={styles.settingsPanel} aria-label="Experience settings">
            <div className={styles.settingsHeader}>
              <h2>Experience settings</h2>
              <p>Adjust the app theme or sign out of your AwkHerd account.</p>
            </div>
            <div className={styles.switcher}>
              <ThemeSwitcher />
            </div>
            <button type="button" className={styles.logoutButton} onClick={handleLogout}>
              Log out
            </button>
            <p className={styles.logoutHint}>
              This signs you out of AwkHerd on this device.
            </p>
          </aside>
        </div>

        {isLoading && <p className={styles.notice}>Loading profile details…</p>}
        {isError && !data && (
          <p className={styles.error}>
            We couldn&apos;t load your profile. Save to create one.
          </p>
        )}
      </section>
    </div>
  );
};

export default ProfilePage;
